import { ablyChannelNamesForSession } from "_shared/_stores/realtime/ably-channel-names";
import reduxAblyMiddleware, { withCallbacks } from "_shared/_stores/realtime/redux-ably-middleware";
import Guid from "_shared/guids/guid";
import { MessageJSON } from "messaging/_schemas/message-schema";
import Message from "messaging/message";
import { Action } from "notifications/notification-action";
import TopicCategory from "notifications/topics/topic-category";
import {
  addComment,
  addCreatedComment,
  removeCommentById,
} from "work/proposals/comments/_store/comments-redux-slice";
import Comment from "work/proposals/comments/comment";
import { addCommentThread } from "work/proposals/comments/threads/_store/comment-thread-redux-slice";

function toMessage(payload: unknown): Message {
  return Message.fromJSON({
    json: payload as MessageJSON,
  });
}
const callbacks = withCallbacks()
  /**
   * A comment as its creation stored it. For its audience this is the first they hear of it; for its
   * author it is the acknowledgement of the draft their composer put on screen — and it can arrive
   * before the post's own response, or after a later write's. `addCreatedComment` applies it only to a
   * draft the server has not yet answered for, so a creation can never roll the comment back past what
   * the author has done to it since. See `applyCreationAnswerInEntries` in the comments slice.
   */
  .add("message-received", (payload: unknown) => (dispatch) => {
    const message = toMessage(payload);
    if (
      message.forum.topic?.entityClass?.value.toLowerCase() === TopicCategory.Work.Proposal.toLowerCase() &&
      message.forum.topic.context?.toLowerCase().includes(Action.Review.toLowerCase())
    ) {
      const comment = Comment.fromMessage(message);
      dispatch(addCreatedComment(comment));
      dispatch(addCommentThread(comment.thread));
    }
  })
  .add("message-updated", (payload: unknown) => (dispatch) => {
    const message = toMessage(payload);
    if (
      !(
        message.forum.topic?.entityClass?.value.toLowerCase() === TopicCategory.Work.Proposal.toLowerCase() &&
        message.forum.topic.context?.toLowerCase().includes(Action.Review.toLowerCase())
      )
    ) {
      return;
    }
    const comment = Comment.fromMessage(message);
    dispatch(addComment(comment));
    dispatch(addCommentThread(comment.thread));
  })
  /**
   * Removal, not deletion — the two are different events despite the name.
   *
   * Every comment that stays in the thread arrives as `message-updated` carrying a tombstone, and the
   * branch above puts it in the store: a soft delete sends one with a `deletedDate` and the pane renders
   * "This comment was deleted", and a retraction sends one with a `retracted` and the pane renders
   * "Removed at ...". Both keep their place, because both keep the `published` instant they were sorted
   * by. A retraction used to come through HERE instead, which is why a comment its author took back
   * vanished off its audience's screen without a word — and why, when it came back, it came back as the
   * newest comment in the conversation.
   *
   * What is left for `message-deleted` is a comment that must leave the reader's view outright: a hard
   * removal of the row, with nothing behind it to mark.
   */
  .add("message-deleted", (payload: unknown) => (dispatch) => {
    const deletedCommentId = new Guid((payload as { id: string }).id);
    dispatch(removeCommentById(deletedCommentId));
  });

export const commentsAblyReduxMiddleware = reduxAblyMiddleware({
  callbacks,
  channelName: ablyChannelNamesForSession.messages,
  shouldConnectionStartImmediately: true,
});

